import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { ApprovalFlowService } from '../../admin-portal/approval-flow-models/approval-flow.service';
import { WorkflowTicket } from '../../admin-portal/approval-flow-models/workflow-ticket.model';
import { NotificationService } from '../../service/notification.service';
import { NotificationType } from '../../enum/notification-type.enum';
import {
  enrichCommissionReportPayrollMonth,
  setCommissionReportApprovalsNavigationContext
} from '../../shared/utils/commission-report-payroll.util';

export interface CommReportApprovalStatusModalData {
  salesChannel?: string | null;
  commPlanId?: number | null;
  calRunId?: string | null;
  payrollMonth?: string;
}

type ApprovalPeriodPreset = 'all' | 'lastWeek' | 'lastMonth' | 'lastYear';

@Component({
  selector: 'app-comm-report-approval-status-modal',
  templateUrl: './comm-report-approval-status-modal.component.html',
  styleUrls: ['./comm-report-approval-status-modal.component.css']
})
export class CommReportApprovalStatusModalComponent implements OnInit {

  loading = false;
  approvals: WorkflowTicket[] = [];

  readonly periodOptions: { value: ApprovalPeriodPreset; label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'lastWeek', label: 'Last Week' },
    { value: 'lastMonth', label: 'Last Month' },
    { value: 'lastYear', label: 'Last Year' }
  ];

  selectedPeriod: ApprovalPeriodPreset = 'all';
  fromDate = '';
  toDate = '';

  constructor(
    private dialogRef: MatDialogRef<CommReportApprovalStatusModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CommReportApprovalStatusModalData,
    private approvalFlowService: ApprovalFlowService,
    private notificationService: NotificationService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.loadApprovalStatus();
  }

  close(): void {
    this.dialogRef.close();
  }

  getTrackingId(item: WorkflowTicket): string {
    return item.trackingId || item.tracking_id || '';
  }

  getStatusClass(status: string): string {
    const normalized = (status || '').toLowerCase();
    if (normalized === 'approved') {
      return 'badge-success';
    }
    if (normalized === 'rejected') {
      return 'badge-danger';
    }
    if (normalized.includes('pending')) {
      return 'badge-warning';
    }
    return 'badge-secondary';
  }

  onPeriodChange(): void {
    this.applyPeriodPreset(this.selectedPeriod);
    this.loadApprovalStatus();
  }

  searchByDateRange(): void {
    if (this.fromDate && !this.toDate) {
      this.notificationService.sendNotification(NotificationType.ERROR, 'Please select To Date');
      return;
    }
    if (this.toDate && !this.fromDate) {
      this.notificationService.sendNotification(NotificationType.ERROR, 'Please select From Date');
      return;
    }
    if (this.fromDate && this.toDate && this.fromDate > this.toDate) {
      this.notificationService.sendNotification(NotificationType.ERROR, 'From Date cannot be after To Date');
      return;
    }

    this.loadApprovalStatus();
  }

  openOnApprovalsPage(item: WorkflowTicket): void {
    const trackingId = this.getTrackingId(item);
    if (!trackingId) {
      this.notificationService.sendNotification(NotificationType.ERROR, 'No tracking ID found for this approval');
      return;
    }

    setCommissionReportApprovalsNavigationContext({
      trackingId,
      salesChannel: item.sales_channel || this.data.salesChannel || null,
      commPlanId: item.complanid != null ? Number(item.complanid) : this.data.commPlanId ?? null
    });

    this.dialogRef.close();
    this.router.navigate(['/approvals']);
  }

  private applyPeriodPreset(preset: ApprovalPeriodPreset): void {
    const today = new Date();

    switch (preset) {
      case 'all':
        this.fromDate = '';
        this.toDate = '';
        break;
      case 'lastWeek':
        this.fromDate = this.formatDate(this.addDays(today, -7));
        this.toDate = this.formatDate(today);
        break;
      case 'lastMonth':
        this.fromDate = this.formatDate(this.addMonths(today, -1));
        this.toDate = this.formatDate(today);
        break;
      case 'lastYear':
        this.fromDate = this.formatDate(this.addYears(today, -1));
        this.toDate = this.formatDate(today);
        break;
    }
  }

  private loadApprovalStatus(): void {
    this.loading = true;
    const employeeId = localStorage.getItem('CURRENT_EMPLOYEEID_SESSION') || '';
    const loggedInRole = localStorage.getItem('CURRENT_USERROLE_SESSION')
      || localStorage.getItem('SESSION_ROLE')
      || 'ADMIN';

    const { fdt, tdt } = this.resolveApiDateRange();

    this.approvalFlowService.getApprovalsData(employeeId, loggedInRole, 'all', fdt, tdt)
      .subscribe(
        (response: WorkflowTicket[]) => {
          this.approvals = enrichCommissionReportPayrollMonth(response)
            .filter(row => row.disputeType === 'Commission Report');
          this.loading = false;
        },
        (errorResponse: HttpErrorResponse) => {
          this.loading = false;
          this.notificationService.sendNotification(
            NotificationType.ERROR,
            errorResponse?.error?.message || errorResponse?.message || 'Failed to load approval status'
          );
        }
      );
  }

  private resolveApiDateRange(): { fdt: string; tdt: string } {
    if (this.fromDate && this.toDate) {
      return { fdt: this.fromDate, tdt: this.toDate };
    }

    return { fdt: '1111-11-11', tdt: '1111-11-11' };
  }

  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private addDays(date: Date, days: number): Date {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  }

  private addMonths(date: Date, months: number): Date {
    const result = new Date(date);
    result.setMonth(result.getMonth() + months);
    return result;
  }

  private addYears(date: Date, years: number): Date {
    const result = new Date(date);
    result.setFullYear(result.getFullYear() + years);
    return result;
  }
}
